import {  Mail, Phone, Scale, MapPin, FileText, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

const Impressum = () => {
  return (
    <div className="bg-white py-24">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-3 bg-success/5 px-4 py-2 rounded-lg mb-4">
              <FileText className="w-5 h-5 text-success" />
              <span className="text-success font-medium">Impressum</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-4">Rechtliche Informationen</h1>
            <p className="text-gray-600">
              Gemäß § 5 TMG (Telemediengesetz) und § 25 Mediengesetz
            </p>
          </div>

          {/* Table of Contents */}
          <nav className="mb-12 p-6 bg-gray-50 rounded-xl">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-success" />
              Inhaltsverzeichnis
            </h2>
            <ul className="space-y-2">
              {[
                { id: 'unternehmen', title: '1. Unternehmensangaben' },
                { id: 'kontakt', title: '2. Kontakt' },
                { id: 'geschaeftsfuehrung', title: '3. Geschäftsführung' },
                { id: 'taetigkeit', title: '4. Tätigkeitsbereich' },
                { id: 'rechtliches', title: '5. Rechtliche Hinweise' }
              ].map(section => (
                <li key={section.id}>
                  <a 
                    href={`#${section.id}`}
                    className="flex items-center space-x-2 text-gray-600 hover:text-success transition-colors"
                  >
                    <span className="w-1.5 h-1.5 bg-success rounded-full" />
                    <span>{section.title}</span>
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Content Sections */}
          <div className="space-y-12">
            {/* Company Information */}
            <section id="unternehmen" className="scroll-mt-24">
              <h2 className="text-2xl font-bold mb-6">1. Unternehmensangaben</h2>
              <div className="prose prose-gray max-w-none">
                <div className="bg-gray-50 rounded-xl p-6 space-y-4">
                  <div>
                    <h3 className="font-semibold mb-2">Firmenname</h3>
                    <p className="text-gray-600">FPHC UmweltConsulting GmbH</p>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Firmenbuchdaten</h3>
                    <p className="text-gray-600">UID-Nummer: ATU66821228</p>
                    <p className="text-gray-600">Firmenbuchnummer: FN370423s</p>
                    <p className="text-gray-600">Firmenbuchgericht: Landesgericht Linz</p>
                    <p className="text-gray-600">Dienstgebernummer: 701864781</p>
                    <p className="text-gray-600">D-U-N-S® Nummer: 300313914</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Contact Information */}
            <section id="kontakt" className="scroll-mt-24">
              <h2 className="text-2xl font-bold mb-6">2. Kontakt</h2>
              <div className="prose prose-gray max-w-none">
                <div className="bg-gray-50 rounded-xl p-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <div className="flex items-start gap-3 mb-4">
                        <MapPin className="w-5 h-5 text-success mt-1" />
                        <div>
                          <h3 className="font-semibold mb-2">Anschrift</h3>
                          <p className="text-gray-600">Tischlerbauerstraße 11</p>
                          <p className="text-gray-600">4060 Leonding / Rufling</p>
                          <p className="text-gray-600">Österreich</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <Phone className="w-5 h-5 text-success mt-1" />
                        <div>
                          <h3 className="font-semibold mb-2">Telefon</h3>
                          <a href="tel:+436644704858" className="text-success hover:text-dark transition-colors">
                            +43 664 470 48 58
                          </a>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Mail className="w-5 h-5 text-success mt-1" />
                        <div>
                          <h3 className="font-semibold mb-2">E-Mail</h3>
                          <div className="space-y-1">
                            <a href="mailto:office@fphc.at" className="block text-success hover:text-dark transition-colors">
                              office@fphc.at
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Management */}
            <section id="geschaeftsfuehrung" className="scroll-mt-24">
              <h2 className="text-2xl font-bold mb-6">3. Geschäftsführung</h2>
              <div className="prose prose-gray max-w-none">
                <div className="bg-gray-50 rounded-xl p-6">
                  <ul className="space-y-2 text-gray-600">
                    <li>Dipl.-Ing. (FH) Florian Pauk</li>
                    <li>Dipl.-Ing. (FH) Birgit Pauk</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Business Information */}
            <section id="taetigkeit" className="scroll-mt-24">
              <h2 className="text-2xl font-bold mb-6">4. Tätigkeitsbereich</h2>
              <div className="prose prose-gray max-w-none">
                <div className="bg-gray-50 rounded-xl p-6 space-y-4">
                  <div>
                    <h3 className="font-semibold mb-2">Unternehmensgegenstand</h3>
                    <ul className="space-y-2 text-gray-600">
                      <li>• Ingenieurbüro (Beratende Ingenieure) für das Fachgebiet Technischer Umweltschutz</li>
                      <li>• Unternehmensberatung einschließlich Unternehmensorganisation im Bereich Umweltmanagement</li>
                      <li>• Akkreditierte Typ-A-Inspektionsstelle gemäß EN ISO/IEC 17020:2012</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Legal Notices */}
            <section id="rechtliches" className="scroll-mt-24">
              <h2 className="text-2xl font-bold mb-6">5. Rechtliche Hinweise</h2>
              <div className="prose prose-gray max-w-none">
                <div className="bg-gray-50 rounded-xl p-6 space-y-4">
                  <p className="text-gray-600">
                    Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den 
                    allgemeinen Gesetzen verantwortlich.
                  </p>
                  <div className="bg-success/5 rounded-xl p-6 mt-6">
                    <div className="flex items-start gap-4">
                      <Shield className="w-6 h-6 text-success flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Datenschutzhinweis</h3>
                        <p className="text-gray-600 text-sm">
                          Detaillierte Informationen zum Umgang mit Ihren Daten finden Sie in unserer{' '}
                          <Link to="/datenschutz" className="text-success hover:text-dark transition-colors">
                            Datenschutzerklärung
                          </Link>.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Footer Navigation */}
          <div className="mt-16 pt-8 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <Link 
                to="/agb"
                className="text-success hover:text-dark transition-colors flex items-center gap-2"
              >
                <Scale className="w-4 h-4" />
                <span>Zu den AGBs</span>
              </Link>
              <Link 
                to="/datenschutz"
                className="text-success hover:text-dark transition-colors flex items-center gap-2"
              >
                <Shield className="w-4 h-4" />
                <span>Zur Datenschutzerklärung</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Impressum;